import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, FileText, Pencil, Plus, RotateCcw, Table2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient, useAuth } from "@/lib/auth";
import { OpsProvider, useOps } from "@/lib/opsCards";
import FormBuilder from "@/components/ops/FormBuilder";
import RecordsTable from "@/components/ops/RecordsTable";

const Shell = ({ testId, children }) => (
  <section data-testid={testId} className="min-h-screen bg-[#0A192F] text-slate-100 py-10 px-6 md:px-10">
    <div className="max-w-5xl mx-auto">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-slate-400 hover:text-brand-cyan font-mono text-[11px] tracking-[0.18em] uppercase mb-6 transition"><ArrowLeft className="w-3.5 h-3.5" /> Back to board</Link>
      {children}
    </div>
  </section>
);

function FormsList() {
  const { forms, counters, reload, ready } = useOps();
  const { user } = useAuth();
  const owner = user?.role === "partner";
  const [editing, setEditing] = useState(null);
  const remove = async (f) => {
    const msg = f.kind === "builtin" ? `Reset “${f.name}” to its default fields?` : `Delete “${f.name}”? Its records stay in the database.`;
    if (!window.confirm(msg)) return;
    await apiClient.delete(`/admin/ops/forms/${f.id}`); toast.success(f.kind === "builtin" ? "Reset" : "Form deleted"); reload();
  };
  if (editing) return (
    <div className="nds-card p-5">
      <div className="font-mono text-[0.6rem] tracking-[0.22em] uppercase text-brand-cyan mb-3">{editing === "new" ? "// New custom form" : `// Edit · ${editing.name}`}</div>
      <FormBuilder form={editing === "new" ? null : editing} onCancel={() => setEditing(null)} onSaved={() => setEditing(null)} />
    </div>
  );
  const row = (f) => (
    <li key={f.id} data-testid={`forms-row-${f.id.replace(":", "-")}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <FileText className="w-4 h-4 text-brand-cyan shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-white">{f.name}</div>
        <div className="text-xs text-slate-400">{f.fields.filter((x) => !x.archived && !x.hidden).length} fields{f.kind === "custom" ? ` · ${counters[f.id] || 0} entries this week` : " · saves to its module"}</div>
      </div>
      {f.kind === "custom" && <Link to={`/admin/records/${f.id}`} data-testid={`forms-records-${f.id}`} className="p-2 text-slate-300 hover:text-brand-cyan" title="Records"><Table2 className="w-4 h-4" /></Link>}
      {owner && <button type="button" onClick={() => setEditing(f)} data-testid={`forms-edit-${f.id.replace(":", "-")}`} className="p-2 text-slate-300 hover:text-brand-cyan" title="Edit"><Pencil className="w-4 h-4" /></button>}
      {owner && <button type="button" onClick={() => remove(f)} data-testid={`forms-delete-${f.id.replace(":", "-")}`} className="p-2 text-slate-500 hover:text-rose-300" title={f.kind === "builtin" ? "Reset" : "Delete"}>{f.kind === "builtin" ? <RotateCcw className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}</button>}
    </li>
  );
  return (
    <>
      <div className="flex items-end justify-between gap-4 mb-6">
        <div>
          <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-brand-cyan">// Form builder</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white mt-1">Quick forms</h1>
          <p className="text-slate-400 mt-1 text-sm">Attach these to any dashboard card — data goes in without leaving the board.</p>
        </div>
        {owner && <button type="button" onClick={() => setEditing("new")} data-testid="forms-new" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-gradient-to-r from-brand-blue to-brand-cyan text-navy-950 font-mono text-[0.65rem] font-bold tracking-[0.14em] uppercase"><Plus className="w-3.5 h-3.5" /> New custom form</button>}
      </div>
      {!ready ? <div className="text-slate-500">Loading…</div> : (
        <>
          <h2 className="font-mono text-[0.6rem] tracking-[0.22em] uppercase text-slate-500 mb-2">Custom forms</h2>
          <ul className="space-y-2 mb-8">{forms.filter((f) => f.kind === "custom").map(row)}</ul>
          {!forms.some((f) => f.kind === "custom") && <p data-testid="forms-custom-empty" className="text-sm text-slate-500 -mt-6 mb-8">No custom forms yet.</p>}
          <h2 className="font-mono text-[0.6rem] tracking-[0.22em] uppercase text-slate-500 mb-2">Built-in actions</h2>
          <ul className="space-y-2">{forms.filter((f) => f.kind === "builtin" && !["documents", "messages"].includes(f.action)).map(row)}</ul>
        </>
      )}
    </>
  );
}

export function FormsPage() {
  return <OpsProvider><Shell testId="forms-page"><FormsList /></Shell></OpsProvider>;
}

function Records() {
  const { formId } = useParams();
  const { formById } = useOps();
  const form = formById(formId);
  return (
    <>
      <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-brand-cyan">// Records</div>
      <h1 data-testid="records-title" className="text-3xl md:text-4xl font-extrabold tracking-tight text-white mt-1 mb-6">{form?.name || "Records"}</h1>
      <RecordsTable formId={formId} />
    </>
  );
}

export function RecordsPage() {
  return <OpsProvider><Shell testId="records-page"><Records /></Shell></OpsProvider>;
}
